/**
 * useNotifications — web notification list state. Wraps the shared
 * notificationApiService (same API the mobile NotificationContext uses)
 * without any FCM/push machinery.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import notificationApiService from '@wlb/shared/api/services/notificationApiService';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { ToastService } from '@wlb/shared/platform/notifier';
import { resolveProfilePictureUrl } from '@wlb/shared/utils/imageUrl';
import { resolveNotificationKind } from '@wlb/shared/utils/notificationHelpers';
import { INVITE_STATUS, isAlreadyHandledInvite } from '@wlb/shared/utils/buddyInvite';
import { timeAgo, getTimezoneOffsetMinutes } from '@wlb/shared/utils/dateHelpers';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import type { NotificationItem } from '@wlb/shared/types/notification';
import {
  extractNotificationRows,
  NOTIFICATIONS_PAGE_SIZE,
  type NotificationRow,
} from './notificationRows';

const mapRawNotification = (item: NotificationRow): NotificationItem => {
  const avatarUrl = resolveProfilePictureUrl(item.actorProfilePicture);

  return {
    ...item,
    id: item.notificationId
      ? String(item.notificationId)
      : item.id
        ? String(item.id)
        : Math.random().toString(),
    type: resolveNotificationKind(item),
    // The API carries the human-readable label in notificationTypeName.
    title: item.notificationTypeName || item.title || '',
    message: item.message || '',
    // Notification reads send `timezoneOffset`; the API answers in device time.
    time: timeAgo(item.createdAt || item.time || Date.now(), 'short', 'local'),
    section: item.section || 'Earlier',
    isRead: Boolean(item.isRead),
    user: item.user || {
      name: item.notificationTypeName || item.title || '',
      avatar: avatarUrl ? { uri: avatarUrl } : null,
    },
    senderId: item.actorUserId || item.senderId,
    notificationId: item.notificationId,
    createdAt: item.createdAt,
    actorProfilePicture: item.actorProfilePicture,
    actorUserId: item.actorUserId,
    actorUserGuid: item.actorUserGuid,
    notificationTypeName: item.notificationTypeName,
    postId: item.postId,
    postCommentId: item.postCommentId,
    groupId: item.groupId,
    conversationId: item.conversationId,
    messageId: item.messageId,
  } as NotificationItem;
};

export interface UseNotificationsResult {
  notifications: NotificationItem[];
  loading: boolean;
  /** Set when the last load failed, so the page can offer a retry. */
  error: string | null;
  fetchNotifications: (isRefresh?: boolean) => Promise<void>;
  markAsRead: (id: string) => Promise<boolean>;
  markAllAsRead: () => Promise<boolean>;
  acceptBuddyRequest: (id: string, senderId?: number) => Promise<void>;
  rejectBuddyRequest: (id: string, senderId?: number) => Promise<void>;
  joinGroupRequest: (id: string) => void;
}

export function useNotifications(): UseNotificationsResult {
  const { t } = useTranslation();
  const { updateInvite } = useProfile();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Whether the list currently has rows, without re-running the fetch on every change. */
  const hasRowsRef = useRef(false);
  const requestRef = useRef<Promise<void> | null>(null);

  const loadNotifications = useCallback(async (isRefresh: boolean) => {
    if (!isRefresh) setLoading(true);
    try {
      const response = await notificationApiService.getNotifications({
        startIndex: 1,
        pageSize: NOTIFICATIONS_PAGE_SIZE,
        timezoneOffset: getTimezoneOffsetMinutes(),
      });

      const rows = extractNotificationRows(response).map(mapRawNotification);
      hasRowsRef.current = rows.length > 0;
      setNotifications(rows);
      setError(null);
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err?.message || t('common.errors.generic');
      setError(message);
      // With nothing on screen the page renders the failure itself; a toast is
      // only needed when the list the member is reading stays behind it.
      if (hasRowsRef.current) ToastService.show({ type: 'error', message });
    } finally {
      if (!isRefresh) setLoading(false);
    }
  }, [t]);

  // The list takes no parameters beyond the page the hook always asks for, so a
  // second caller arriving mid-flight — a remount replaying the mount effect, a
  // retry tapped while the first attempt is still open — waits on that request
  // instead of racing it.
  const fetchNotifications = useCallback(
    async (isRefresh = false): Promise<void> => {
      const pending = requestRef.current;
      if (pending) return pending;
      const request = loadNotifications(isRefresh).finally(() => {
        requestRef.current = null;
      });
      requestRef.current = request;
      return request;
    },
    [loadNotifications],
  );

  useEffect(() => {
    fetchNotifications().catch(() => undefined);
  }, [fetchNotifications]);

  const markAsRead = useCallback(async (id: string): Promise<boolean> => {
    const notif = notifications.find(n => n.id === id);
    if (!notif || notif.isRead) return true;

    // The API reads a missing notificationId as "mark every notification read",
    // so a row the payload never gave one for is left alone rather than marked
    // with an id derived from the display key.
    const numericId = Number(notif.notificationId);
    if (!Number.isFinite(numericId)) return true;

    try {
      await notificationApiService.updateReadStatus({ notificationId: numericId, isRead: true });
    } catch (err: any) {
      ToastService.show({
        type: 'error',
        message: err?.response?.data?.message || err?.message || t('common.errors.generic'),
      });
      return false;
    }

    setNotifications(prev => prev.map(n => (n.id === id ? { ...n, isRead: true } : n)));
    return true;
  }, [notifications, t]);

  const markAllAsRead = useCallback(async (): Promise<boolean> => {
    const hasUnread = notifications.some(n => !n.isRead);
    if (!hasUnread) return false;

    try {
      await notificationApiService.updateReadStatus({ isRead: true });
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      return true;
    } catch (err: any) {
      ToastService.show({
        type: 'error',
        message: err?.response?.data?.message || err?.message || t('common.errors.generic'),
      });
      return false;
    }
  }, [notifications, t]);

  const respondToBuddyRequest = useCallback(
    async (id: string, senderId: number | undefined, accept: boolean): Promise<void> => {
      if (!senderId) {
        ToastService.show({
          type: 'error',
          message: t('common.errors.generic'),
        });
        return;
      }
      try {
        await updateInvite(senderId, accept ? INVITE_STATUS.accept : INVITE_STATUS.reject);
      } catch (err: any) {
        if (!isAlreadyHandledInvite(err)) {
          ToastService.show({
            type: 'error',
            message: err?.response?.data?.message || err?.message || t('common.errors.generic'),
          });
          return;
        }
      }
      setNotifications(prev =>
        prev.map(n =>
          n.id === id ? { ...n, isRead: true, status: accept ? 'accepted' : 'rejected' } : n,
        ),
      );
      ToastService.show({
        type: 'success',
        message: t(accept ? 'home.notifications.toasts.buddyAccepted' : 'home.notifications.toasts.buddyRejected'),
      });
    },
    [updateInvite, t],
  );

  const acceptBuddyRequest = useCallback(
    (id: string, senderId?: number): Promise<void> => respondToBuddyRequest(id, senderId, true),
    [respondToBuddyRequest],
  );

  const rejectBuddyRequest = useCallback(
    (id: string, senderId?: number): Promise<void> => respondToBuddyRequest(id, senderId, false),
    [respondToBuddyRequest],
  );

  const joinGroupRequest = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, isRead: true, status: 'joined' } : n)),
    );
  }, []);

  return {
    notifications,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    acceptBuddyRequest,
    rejectBuddyRequest,
    joinGroupRequest,
  };
}
